import { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTour } from '../../contexts/TourContext';
import { Button } from '../ui/button';
import {
  X,
  ArrowLeft,
  ArrowRight,
  SkipForward,
} from 'lucide-react';
import { cn } from '../../lib/utils';

interface TooltipPosition {
  top: number;
  left: number;
  arrowPosition: 'top' | 'bottom' | 'left' | 'right';
}

export function GuidedTour() {
  const {
    isTourActive,
    currentStepIndex,
    tourSteps,
    nextStep,
    prevStep,
    skipTour,
    endTour,
    // Section tour
    isSectionTourActive,
    currentSectionTour,
    sectionTourStepIndex,
    nextSectionStep,
    prevSectionStep,
    skipSectionTour,
    endSectionTour,
  } = useTour();

  const [tooltipPosition, setTooltipPosition] = useState<TooltipPosition | null>(null);
  const [highlightRect, setHighlightRect] = useState<DOMRect | null>(null);

  // Determine which tour is active and get the appropriate step
  const isAnyTourActive = isTourActive || isSectionTourActive;
  const activeSteps = isSectionTourActive
    ? (currentSectionTour?.steps || [])
    : tourSteps;
  const activeStepIndex = isSectionTourActive ? sectionTourStepIndex : currentStepIndex;
  const currentStep = activeSteps[activeStepIndex];
  const isLastStep = activeStepIndex === activeSteps.length - 1;
  const isFirstStep = activeStepIndex === 0;

  // Unified handlers
  const handleNext = isSectionTourActive ? nextSectionStep : nextStep;
  const handlePrev = isSectionTourActive ? prevSectionStep : prevStep;
  const handleSkip = isSectionTourActive ? skipSectionTour : skipTour;
  const handleEnd = isSectionTourActive ? endSectionTour : endTour;
  const tourTitle = isSectionTourActive ? currentSectionTour?.title : 'App Tour';

  const calculatePosition = useCallback(() => {
    if (!currentStep) return;

    const targetElement = document.querySelector(currentStep.target);
    if (!targetElement) {
      // If target not found, log warning but don't auto-skip (gives user chance to see what's happening)
      console.warn(`Tour target not found: ${currentStep.target}`);
      // Try to position in center of screen as fallback
      setHighlightRect(null);
      setTooltipPosition({
        top: window.innerHeight / 2 - 100,
        left: window.innerWidth / 2 - 160,
        arrowPosition: 'top',
      });
      return;
    }

    const rect = targetElement.getBoundingClientRect();
    setHighlightRect(rect);

    const tooltipWidth = 320;
    const tooltipHeight = 200;
    const padding = 12;
    const arrowSize = 8;

    let top = 0;
    let left = 0;
    let arrowPosition: TooltipPosition['arrowPosition'] = 'top';

    const placement = currentStep.placement || 'right';

    switch (placement) {
      case 'right':
        top = rect.top + rect.height / 2 - tooltipHeight / 2;
        left = rect.right + padding + arrowSize;
        arrowPosition = 'left';
        break;
      case 'left':
        top = rect.top + rect.height / 2 - tooltipHeight / 2;
        left = rect.left - tooltipWidth - padding - arrowSize;
        arrowPosition = 'right';
        break;
      case 'bottom':
        top = rect.bottom + padding + arrowSize;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
        arrowPosition = 'top';
        break;
      case 'top':
        top = rect.top - tooltipHeight - padding - arrowSize;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
        arrowPosition = 'bottom';
        break;
    }

    // Keep tooltip within viewport
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    if (left < padding) left = padding;
    if (left + tooltipWidth > viewportWidth - padding) {
      left = viewportWidth - tooltipWidth - padding;
    }
    if (top < padding) top = padding;
    if (top + tooltipHeight > viewportHeight - padding) {
      top = viewportHeight - tooltipHeight - padding;
    }

    setTooltipPosition({ top, left, arrowPosition });
  }, [currentStep]);

  // Recalculate position when step changes or window resizes
  useEffect(() => {
    if (!isAnyTourActive) return;

    calculatePosition();

    const handleResize = () => calculatePosition();
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize, true);

    // Recalculate after a short delay to account for animations
    const timeoutId = setTimeout(calculatePosition, 100);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize, true);
      clearTimeout(timeoutId);
    };
  }, [isAnyTourActive, activeStepIndex, calculatePosition]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isAnyTourActive) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          handleSkip();
          break;
        case 'ArrowRight':
        case 'Enter':
          handleNext();
          break;
        case 'ArrowLeft':
          handlePrev();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isAnyTourActive, handleNext, handlePrev, handleSkip]);

  if (!isAnyTourActive || !currentStep || !tooltipPosition) return null;

  const getArrowClasses = () => {
    const base = 'absolute w-3 h-3 bg-white border rotate-45';
    switch (tooltipPosition.arrowPosition) {
      case 'top':
        return cn(base, '-top-1.5 left-1/2 -translate-x-1/2 border-l border-t');
      case 'bottom':
        return cn(base, '-bottom-1.5 left-1/2 -translate-x-1/2 border-r border-b');
      case 'left':
        return cn(base, '-left-1.5 top-1/2 -translate-y-1/2 border-l border-b');
      case 'right':
        return cn(base, '-right-1.5 top-1/2 -translate-y-1/2 border-r border-t');
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] pointer-events-none">
      {/* Overlay with spotlight effect */}
      <div className="absolute inset-0 bg-black/50 pointer-events-auto">
        {highlightRect && (
          <div
            className="absolute bg-transparent transition-all duration-200"
            style={{
              top: highlightRect.top - 4,
              left: highlightRect.left - 4,
              width: highlightRect.width + 8,
              height: highlightRect.height + 8,
              boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)',
              borderRadius: '8px',
              border: '2px solid #3b82f6',
            }}
          />
        )}
      </div>

      {/* Tooltip */}
      <div
        className="absolute bg-white rounded-lg shadow-2xl border p-4 pointer-events-auto w-80"
        style={{
          top: tooltipPosition.top,
          left: tooltipPosition.left,
        }}
      >
        {/* Arrow */}
        <div className={getArrowClasses()} />

        {/* Close button */}
        <button
          onClick={handleSkip}
          className="absolute top-2 right-2 p-1 hover:bg-slate-100 rounded"
        >
          <X className="h-4 w-4 text-muted-foreground" />
        </button>

        {/* Tour title badge for section tours */}
        {isSectionTourActive && tourTitle && (
          <div className="mb-2">
            <span className="text-xs font-medium px-2 py-0.5 bg-primary/10 text-primary rounded">
              {tourTitle}
            </span>
          </div>
        )}

        {/* Content */}
        <div className="pr-6">
          <h3 className="font-semibold text-lg mb-1">{currentStep.title}</h3>
          <p className="text-sm text-muted-foreground mb-4">{currentStep.content}</p>
        </div>

        {/* Progress indicator */}
        <div className="flex items-center gap-1 mb-4">
          {activeSteps.map((_, idx) => (
            <div
              key={idx}
              className={cn(
                'h-1.5 rounded-full transition-all',
                idx === activeStepIndex
                  ? 'w-6 bg-primary'
                  : idx < activeStepIndex
                  ? 'w-1.5 bg-primary/50'
                  : 'w-1.5 bg-slate-200'
              )}
            />
          ))}
        </div>

        {/* Navigation buttons */}
        <div className="flex items-center justify-between">
          <div className="text-xs text-muted-foreground">
            Step {activeStepIndex + 1} of {activeSteps.length}
          </div>
          <div className="flex items-center gap-2">
            {!isFirstStep && (
              <Button variant="outline" size="sm" onClick={handlePrev}>
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
            )}
            {isLastStep ? (
              <Button size="sm" onClick={handleEnd}>
                Finish Tour
              </Button>
            ) : (
              <Button size="sm" onClick={handleNext}>
                Next
                <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            )}
          </div>
        </div>

        {/* Skip link */}
        <button
          onClick={handleSkip}
          className="mt-3 text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
        >
          <SkipForward className="h-3 w-3" />
          Skip tour
        </button>
      </div>
    </div>,
    document.body
  );
}
